import { globalAny, toReversed } from "../../utils/Utils";
import CreateAccountButton from "../CreateAccountButton";
import ComponentThemeType from "../../models/ComponentTheme";
import TouchableButton from "../TouchableButton";
import ComponentTypeEnum from "../../models/ComponentTypeEnum";
import PrivacyPolicyButton from "../PrivacyPolicyButton";
import {
	StyledButtonContainer,
	StyledLegalTitleStyle,
	StyledNarrowBodyContent,
	StyledNarrowButton,
	StyledNarrowContent,
	StyledNarrowCreateButton,
	StyledNarrowMainContent,
	StyledNarrowTextContent,
	StyledWideBodyContent,
	StyledWideContent,
	StyledWideCreateButton,
	StyledWideFooter,
	StyledWideTextContent,
} from "./styled";
import useGenericContentStore from "../../store/genericContent.store";
import { Props } from "./model";
import WideCreateAccountWrapper from "../Layout/WideCreateAccountWrapper";
import NarrowCreateAccountWrapper from "../Layout/NarrowCreateAccountWrapper";
import { TouchableButtonDataProps } from "../../models/TouchableButtonDataProps";

const LegalTermsConditions = (props: Props) => {
	const { isNarrow } = useGenericContentStore((state) => ({ isNarrow: state.isNarrow }));
	const createAccountButtonData = [
		{ title: globalAny.language.terms_of_use, onPress: props.onPressTermsOfUse },
		{ title: globalAny.language.terms_of_service, onPress: props.onPressTermsOfService },
	];

	const touchableButtonData: TouchableButtonDataProps[] = [
		{
			title: globalAny.language.back,
			type: ComponentTypeEnum.Secondary,
			disabled: false,
			leftText: isNarrow,
			onPress: props.onPressBack,
		},
		{
			title: globalAny.language.i_agree,
			type: ComponentTypeEnum.Primary,
			disabled: false,
			leftText: isNarrow,
			onPress: props.onPressNext,
		},
	];

	/**
	 * Renders a single CreateAccountButton for legal terms.
	 *
	 * @param {Object} params - The button properties.
	 * @param {string} params.title - The title text for the button.
	 * @param {() => void} params.onPress - Callback function when the button is pressed.
	 * @returns JSX.Element - The rendered CreateAccountButton component.
	 */
	const renderCreateAccountButton = ({ title, onPress }: any) => (
		<CreateAccountButton key={`legal-term-button${title}`} title={title} onPress={onPress} />
	);

	/**
	 * Renders a single TouchableButton for legal terms.
	 *
	 * @param {Object} params - The button properties.
	 * @param {string} params.title - The text displayed on the button.
	 * @param {string} params.type - The button type (e.g., primary or secondary).
	 * @param {boolean} params.leftText - Whether to align text to the left.
	 * @param {boolean} params.disabled - Whether the button is disabled.
	 * @param {() => void} params.onPress - Callback function when the button is pressed.
	 * @returns JSX.Element - The rendered TouchableButton component.
	 */
	const renderTouchableButtonData = ({ title, type, leftText, disabled, onPress }: TouchableButtonDataProps) => (
		<TouchableButton
			key={`legal-term-touchable-${title}`}
			leftText={leftText}
			componentTheme={ComponentThemeType.VinFast}
			type={type}
			onPress={onPress}
			disabled={disabled}
		>
			{title}
		</TouchableButton>
	);

	/**
	 * Renders the wide screen layout for the Legal Terms component.
	 *
	 * Includes:
	 * - The main content with title, body text, and account-related buttons.
	 * - Optional footer with additional buttons and a privacy policy link.
	 *
	 * @returns JSX.Element - The rendered wide screen layout.
	 */
	const renderWideScreen = () => (
		<WideCreateAccountWrapper>
			<StyledWideContent>
				<StyledWideTextContent>
					{!props.hideTitle && <StyledLegalTitleStyle isNarrow={isNarrow}>{globalAny.language.legal_terms}</StyledLegalTitleStyle>}
					<StyledWideBodyContent>{props.bodyText}</StyledWideBodyContent>
					<StyledWideCreateButton>
						{createAccountButtonData.map((item) => {
							return renderCreateAccountButton(item);
						})}
					</StyledWideCreateButton>
				</StyledWideTextContent>
			</StyledWideContent>

			{props.showButton && (
				<StyledWideFooter>
					<StyledButtonContainer>
						{touchableButtonData.map((item) => {
							return renderTouchableButtonData(item);
						})}
					</StyledButtonContainer>
					<PrivacyPolicyButton onPress={props.onPressPrivacy} />
				</StyledWideFooter>
			)}
		</WideCreateAccountWrapper>
	);
	/**
	 * Renders the narrow screen layout for the Legal Terms component.
	 *
	 * Includes:
	 * - Optional top section with buttons and a privacy policy link (if `showButton` is true).
	 * - Main content with title, body text, and account-related buttons.
	 *
	 * @returns JSX.Element - The rendered narrow screen layout.
	 */
	const renderNarrowScreen = () => (
		<NarrowCreateAccountWrapper>
			{props.showButton && (
				<StyledNarrowButton>
					<StyledNarrowCreateButton>
						{toReversed(touchableButtonData).map((item) => {
							return renderTouchableButtonData(item);
						})}
					</StyledNarrowCreateButton>
					<PrivacyPolicyButton onPress={props.onPressPrivacy} />
				</StyledNarrowButton>
			)}

			<StyledNarrowMainContent>
				<StyledNarrowContent>
					<StyledNarrowTextContent>
						{!props.hideTitle && <StyledLegalTitleStyle isNarrow={isNarrow}>{globalAny.language.legal_terms}</StyledLegalTitleStyle>}
						<StyledNarrowBodyContent>{props.bodyText}</StyledNarrowBodyContent>
						<StyledNarrowCreateButton isPaddingLeft>
							{createAccountButtonData.map((item) => {
								return renderCreateAccountButton(item);
							})}
						</StyledNarrowCreateButton>
					</StyledNarrowTextContent>
				</StyledNarrowContent>
			</StyledNarrowMainContent>
		</NarrowCreateAccountWrapper>
	);
	return isNarrow ? renderNarrowScreen() : renderWideScreen();
};

export default LegalTermsConditions;
